import {
  Navigate,
  useLocation,
  useNavigate,
  useSearchParams,
} from 'react-router-dom';
import { leagueGroupName } from 'helpers/leagueGroupName';
import styles from '../styles/pages/LeaguesList.module.css';
import { MobileLayout } from 'components/mobile/MobileLayout/MobileLayout';
import { capitalizeFirstWord } from 'helpers/stringHelper';
import { useEffect, useMemo, useRef, useState } from 'react';
import { League } from 'types/League';
import useAsyncEffect from 'use-async-effect';
import {
  getLeaguesForSidebar,
  searchEventsByTeamName,
} from 'services/api/sportBetsService';
import { Loader } from 'components/Loader/Loader';
import { useBreakpoint } from 'hooks/useBreakpoint';
import { useTranslation } from 'react-i18next';
import { sportTranslationKey } from 'helpers/sportI18n';
import { SearchResults } from 'components/mobile/SearchResults/SearchResults';
import { filterPrematchSearchBySport } from 'helpers/filterPrematchSearchBySport';
import type { MatchOffer } from 'types/MatchOffer';
import { SportIcon } from 'components/ThemeIcon/SportIcon';
import { ThemeIcon } from 'components/ThemeIcon/ThemeIcon';

const SEARCH_DEBOUNCE_MS = 350;
const MIN_SEARCH_QUERY_LENGTH = 2;

type SidebarLeague = {
  name: string;
  id: number;
  count: number;
};

type SidebarGroup = {
  name: string;
  id: number;
  count: number;
  subList: SidebarLeague[];
};

/** Mobile: same drill-down as sports betting leagues, counts from results window. */
export const ResultsLeaguesList = () => {
  const { t, i18n } = useTranslation();
  const urlParts = useLocation().pathname.split('/');
  const navigate = useNavigate();
  const { isMobile } = useBreakpoint();
  const sportId = urlParts.pop() ?? '1';
  const sport = urlParts.pop()!;

  const sportIdNumber = useMemo(() => Number.parseInt(sportId, 10), [sportId]);

  const [searchParams] = useSearchParams();
  const time = searchParams.get('time');

  const [groupedLeagues, setGroupedLeagues] = useState<SidebarGroup[] | null>(
    null
  );
  const [expandedGroups, setExpandedGroups] = useState<Record<number, boolean>>(
    {}
  );
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const searchInputRef = useRef<HTMLInputElement>(null);
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const searchSeqRef = useRef(0);

  const [searchResults, setSearchResults] = useState<Map<
    number,
    MatchOffer[]
  > | null>(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [emptySearchQuery, setEmptySearchQuery] = useState<string | null>(null);

  useAsyncEffect(async () => {
    const leaguesData = await getLeaguesForSidebar(
      sportIdNumber,
      undefined,
      true
    );
    if (!leaguesData) {
      setGroupedLeagues(null);
      return;
    }

    const groups: SidebarGroup[] = leaguesData.every((item: any) =>
      Array.isArray(item?.subList)
    )
      ? leaguesData
      : [
          {
            name: t('league'),
            id: -sportIdNumber,
            count: leaguesData.length,
            subList: (leaguesData as League[]).map((league) => ({
              name: league.name,
              id: league.id,
              count: league.count,
            })),
          },
        ];

    setGroupedLeagues(groups);
    setExpandedGroups(
      groups.reduce((acc, group) => ({ ...acc, [group.id]: false }), {})
    );
  }, [sportIdNumber, t]);

  useEffect(() => {
    if (searchOpen) {
      searchInputRef.current?.focus();
    } else {
      setSearchQuery('');
    }
  }, [searchOpen]);

  useEffect(() => {
    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!searchOpen) {
      searchSeqRef.current += 1;
      setSearchResults(null);
      setEmptySearchQuery(null);
      setSearchLoading(false);
      return;
    }

    const q = searchQuery.trim();
    if (q.length < MIN_SEARCH_QUERY_LENGTH) {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
        searchDebounceRef.current = null;
      }
      searchSeqRef.current += 1;
      setSearchResults(null);
      setEmptySearchQuery(null);
      setSearchLoading(false);
      return;
    }

    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
      searchDebounceRef.current = null;
    }

    searchDebounceRef.current = setTimeout(() => {
      searchDebounceRef.current = null;
      const mySeq = ++searchSeqRef.current;
      const query = searchQuery.trim();
      setSearchLoading(true);

      void (async () => {
        try {
          const raw = await searchEventsByTeamName(query, false, true);
          if (mySeq !== searchSeqRef.current) return;
          if (!raw) {
            setSearchResults(null);
            setEmptySearchQuery(query);
            setSearchLoading(false);
            return;
          }
          const filtered = filterPrematchSearchBySport(
            raw as Map<number, MatchOffer[]>,
            sportIdNumber
          );
          if (filtered.size > 0) {
            setSearchResults(filtered);
            setEmptySearchQuery(null);
          } else {
            setSearchResults(null);
            setEmptySearchQuery(query);
          }
        } catch {
          if (mySeq !== searchSeqRef.current) return;
          setSearchResults(null);
          setEmptySearchQuery(query);
        }
        setSearchLoading(false);
      })();
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
        searchDebounceRef.current = null;
      }
    };
  }, [searchOpen, searchQuery, sportIdNumber]);

  if (!isMobile) {
    return <Navigate to="/not-found" />;
  }

  const showMinCharsHint =
    searchOpen &&
    searchQuery.trim().length > 0 &&
    searchQuery.trim().length < MIN_SEARCH_QUERY_LENGTH;

  return (
    <MobileLayout
      pageTitle={t(sportTranslationKey(sportIdNumber), {
        defaultValue: capitalizeFirstWord(sport),
      })}
      headerTrailing={
        <button
          type="button"
          className={styles.searchToggle}
          onClick={() => setSearchOpen((o) => !o)}
          aria-expanded={searchOpen}
          aria-label={t('search_label')}
        >
          <ThemeIcon
            icon="search"
            alt=""
            variant="inline"
            tone="chrome"
            width={22}
            height={22}
          />
        </button>
      }
      topContent={
        searchOpen ? (
          <div className={styles.searchPanel}>
            <input
              ref={searchInputRef}
              type="search"
              className={styles.searchInput}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t('search_matches_placeholder')}
              enterKeyHint="search"
              autoComplete="off"
              autoCorrect="off"
              spellCheck={false}
            />
          </div>
        ) : null
      }
    >
      {!searchOpen && (
        <div className={styles.wrapper}>
          {!groupedLeagues && <Loader />}
          {groupedLeagues &&
            groupedLeagues.map((group) => (
              <div key={group.id}>
                <div
                  className={styles.countryWrapper}
                  onClick={() =>
                    setExpandedGroups((prev) => ({
                      ...prev,
                      [group.id]: !prev[group.id],
                    }))
                  }
                >
                  {sport && (
                    <SportIcon
                      sportId={sportIdNumber}
                      tone="content"
                      className={styles.countrySportIcon}
                      alt={sport}
                    />
                  )}
                  <div className={styles.title}>
                    {leagueGroupName(
                      group.name,
                      (group as any).groupCode,
                      i18n.language
                    )}
                  </div>
                  <div className={styles.count}>{group.count}</div>
                </div>
                {expandedGroups[group.id] &&
                  group.subList.map((league) => (
                    <div
                      key={league.id}
                      className={styles.leagueWrapper}
                      onClick={() =>
                        navigate(
                          `/results/${sport}/${sportId}/${league.name}/${league.id}${time ? `?time=${time}` : ''}`
                        )
                      }
                    >
                      <div className={styles.leagueTitle}>{league.name}</div>
                      <div className={styles.count}>{league.count}</div>
                    </div>
                  ))}
              </div>
            ))}
        </div>
      )}

      {searchOpen && (
        <div className={styles.searchBody}>
          {showMinCharsHint && (
            <div className={styles.searchHint}>
              <ThemeIcon
                icon="info-circle"
                alt=""
                variant="inline"
                tone="content"
              />
              <span>{t('betting_search_error')}</span>
            </div>
          )}
          {searchLoading &&
            searchQuery.trim().length >= MIN_SEARCH_QUERY_LENGTH && <Loader />}
          {!searchLoading &&
            searchResults &&
            searchResults.size > 0 &&
            searchQuery.trim().length >= MIN_SEARCH_QUERY_LENGTH && (
              <SearchResults events={searchResults} liveSearch={false} />
            )}
          {!searchLoading &&
            emptySearchQuery &&
            searchQuery.trim().length >= MIN_SEARCH_QUERY_LENGTH && (
              <div className={styles.emptySearchInline}>
                <ThemeIcon
                  icon="info-circle"
                  alt=""
                  variant="inline"
                  tone="content"
                />
                <span>
                  {t('no_search_results').replace(
                    '{search_query}',
                    emptySearchQuery
                  )}
                </span>
              </div>
            )}
        </div>
      )}
    </MobileLayout>
  );
};
